import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {WindowKey} from "@bizform/type/model/model/WindowKey.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {ComponentHelper, Debug, ObjectHelper} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, DataStorage, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {I18n} from "../../misc/model/I18n.model";


type Props = {
};

type WrappedCompState = {
    id?: string;
    tabindex?: number;
    src?: string;
    subscriptionValue?: string;
    alt?: string;
    data?: string;
    className?: string;
    style?: object;
    role?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    onClick?: (e) => void;
};

type WrappedFormState = {
    onClick?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi, Hwansoon Park
 * @class BfImage
 * @description BfImage component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfImage extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_RJlpfhlF3xmuMOC_QbpoUVAJiJSH9Ef",
        category: ComponentCategory.LABEL,
        label: I18n.componentElement.image.label,
        description: I18n.componentElement.image.description,
        icon: "",
        dataType: [DataType.STRING],
        property: null
    };

    focus(): void {
        if (this._image && this._image.focus) {
            this._image.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            this._removeCustom();
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(
                property,
                this);
            if (newState.wrappedCompState.src) {
                newState.wrappedCompState.src = this._srcMatcher(newState.wrappedCompState.src);
            }
            if (!newState.wrappedCompState.role) {
                delete(newState.wrappedCompState.role);
            }
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string): void {
        const subscriptionValue = this._srcMatcher(value);
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                subscriptionValue
            }
        };
        this.setState(newState);
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfImage)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _image: any;
    private _tooltip: Array<any> = [];

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                src: null,
                subscriptionValue: null
            },
            wrappedFormState: {
                onClick: null
            }
        };

        this._setRef = this._setRef.bind(this);
        this._onClickEvent = this._onClickEvent.bind(this);
        this._onKeyDown = this._onKeyDown.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate(): void {
        if (this._image && !this._tooltip.length) {
            ComponentHelper.tooltip(this.parentDom, this.state.wrappedCompState.tooltipAttribute, this._tooltip);
        }
    }

    componentWillUnmount(): void {
        this._image = null;
        this._removeCustom();
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                case ComponentElementDisplayMode.PRINT:
                    return this._render();
                default:
                    return null;
            }
        }
    }

    private _render(): React.ReactNode {
        const url = this.state.wrappedCompState.subscriptionValue ? this.state.wrappedCompState.subscriptionValue : this.state.wrappedCompState.src ? this.state.wrappedCompState.src : "css/image_default.png";
        const tabindex = this.state.wrappedCompState.tabindex;
        const newProps = {...this.state.wrappedCompState};
        delete(newProps.tooltipAttribute);
        return (
            <img
                {...newProps}
                id={this.elementId}
                src={url}
                title={this.state.wrappedCompState.alt}
                className={this.state.wrappedCompState.className}
                style={{ ...this.state.wrappedCompState.style }}
                onClick={this.state.wrappedCompState.onClick}
                onKeyDown={tabindex > 0 || tabindex === 0 ? this._onKeyDown : null}
                ref={this._setRef}
                draggable={window[WindowKey.DESIGNMODE] ? false : true}
            />
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._removeCustom();
            this._image = ref;
            this.readyComponent();
        }
    }

    private _removeCustom(): void {
        if (this._tooltip.length) {
            ComponentHelper.unbindTooltip(this._tooltip);
        }
    }

    private _onClickEvent(): void {
    }

    private _onKeyDown(e: any): void {
        if (this._image && this._image.click && e && e.key === "Enter") {
            this._image.click();
        }
    }

    private _srcMatcher(src: string) {
        if (src && src.match) {
            return src.match(/^([a-zA-Z]{0,}):\/\//) || src.match(/^\//) ? src : location.origin + DataStorage.path + src;
        }
        return src;
    }
}
